import React from "react";
import { Phone, MessageCircle, Instagram, MapPin, Clock } from "lucide-react";
import { BUSINESS, WHATSAPP_URL, TEL_PRIMARY, TEL_SECONDARY, IMAGES, AREAS } from "../lib/business";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-white border-t-4 border-[#E50914]">
      {/* Big closer CTA */}
      <div className="relative overflow-hidden bg-black">
        <div className="absolute inset-0 stripes opacity-10" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20 text-center">
          <h2 className="font-anton text-4xl md:text-6xl lg:text-7xl uppercase text-white leading-[0.9] mb-4">
            Refúgio Guincho 24 Horas
          </h2>
          <p className="font-anton text-xl md:text-3xl uppercase text-[#E50914] tracking-wider mb-8">
            Socorro Rápido · Menor Preço da Região
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-xl mx-auto mb-4">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp-button"
              className="bg-[#25D366] hover:bg-[#1EBE5D] text-white font-anton uppercase text-lg md:text-xl tracking-wider px-7 py-4 inline-flex items-center justify-center gap-2 transition-all hover:-translate-y-1 rounded-sm flex-1"
            >
              <MessageCircle className="w-5 h-5" strokeWidth={2.5} />
              WhatsApp
            </a>
            <a
              href={TEL_PRIMARY}
              data-testid="footer-call-button"
              className="bg-[#E50914] hover:bg-[#B80710] text-white font-anton uppercase text-lg md:text-xl tracking-wider px-7 py-4 inline-flex items-center justify-center gap-2 transition-all hover:-translate-y-1 rounded-sm flex-1"
            >
              <Phone className="w-5 h-5" strokeWidth={2.5} />
              {BUSINESS.phonePrimary}
            </a>
          </div>
          <a
            href={TEL_SECONDARY}
            className="font-anton text-xl text-white/80 hover:text-[#E50914] inline-flex items-center gap-2 transition-colors"
          >
            <Phone className="w-4 h-4" /> {BUSINESS.phoneSecondary}
          </a>
        </div>
      </div>

      <div className="border-t border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16 grid md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-1">
                <img src={IMAGES.logo} alt="Refúgio Guincho logo" className="h-12 w-auto" />
              </div>
              <div>
                <div className="font-anton text-2xl text-black uppercase">Refúgio Guincho</div>
                <div className="font-work text-xs text-[#E50914] uppercase tracking-widest font-bold">
                  O Menor Preço do Mercado
                </div>
              </div>
            </div>
            <p className="font-work text-zinc-700 text-sm leading-relaxed max-w-md mb-5">
              Guincho 24 horas e Centro Automotivo em Araçoiaba da Serra, Sorocaba, Votorantim, São Paulo e toda região. Cobrimos qualquer orçamento da concorrência.
            </p>
            <div className="flex gap-3">
              <a
                href={BUSINESS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                aria-label="Instagram"
                className="w-10 h-10 border-2 border-zinc-300 hover:border-[#E50914] hover:bg-[#E50914] flex items-center justify-center transition-all group"
              >
                <Instagram className="w-5 h-5 text-black group-hover:text-white" />
              </a>
              <a
                href={`https://www.google.com/maps?q=${encodeURIComponent(BUSINESS.mapsQuery)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-maps"
                aria-label="Google Maps"
                className="w-10 h-10 border-2 border-zinc-300 hover:border-[#E50914] hover:bg-[#E50914] flex items-center justify-center transition-all group"
              >
                <MapPin className="w-5 h-5 text-black group-hover:text-white" />
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
                className="w-10 h-10 border-2 border-zinc-300 hover:border-[#25D366] hover:bg-[#25D366] flex items-center justify-center transition-all group"
              >
                <MessageCircle className="w-5 h-5 text-black group-hover:text-white" />
              </a>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-anton text-lg text-black uppercase tracking-wider mb-4">Contato</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <Phone className="w-4 h-4 text-[#E50914] mt-1" />
                <a href={TEL_PRIMARY} className="font-work text-zinc-800 hover:text-[#E50914] text-sm font-semibold">
                  {BUSINESS.phonePrimary}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Phone className="w-4 h-4 text-[#E50914] mt-1" />
                <a href={TEL_SECONDARY} className="font-work text-zinc-800 hover:text-[#E50914] text-sm font-semibold">
                  {BUSINESS.phoneSecondary}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-[#E50914] mt-1 shrink-0" />
                <span className="font-work text-zinc-700 text-sm">
                  {BUSINESS.address}<br />
                  {BUSINESS.city} – {BUSINESS.state}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-[#E50914] mt-1" />
                <span className="font-work text-zinc-700 text-sm">24 horas · 7 dias</span>
              </li>
              <li className="flex items-start gap-2">
                <Instagram className="w-4 h-4 text-[#E50914] mt-1" />
                <a
                  href={BUSINESS.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-work text-zinc-800 hover:text-[#E50914] text-sm"
                >
                  {BUSINESS.instagramHandle}
                </a>
              </li>
            </ul>
          </div>

          {/* Areas */}
          <div>
            <h4 className="font-anton text-lg text-black uppercase tracking-wider mb-4">Áreas Atendidas</h4>
            <ul className="space-y-2">
              {AREAS.map((a) => (
                <li key={a} className="font-work text-zinc-700 text-sm">
                  · {a}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-zinc-200 py-6 bg-zinc-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col md:flex-row justify-between items-center gap-2">
            <p className="font-work text-zinc-600 text-xs text-center md:text-left">
              © {new Date().getFullYear()} Refúgio Guincho · Todos os direitos reservados · Guincho 24h em Araçoiaba da Serra, Sorocaba, Votorantim, São Paulo e região.
            </p>
            <p className="font-work text-zinc-500 text-xs">Atendimento imediato · Menor preço garantido</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
